import React, { useState, useEffect, useMemo } from "react";
import PrimaryHeading from '../../../components/common/primaryHeading';
import ButtonWithSearch from '../../../components/common/buttonWithSearch';
import DataGrid from '../../../components/common/dataGrid/dataGrid';
import PrimarySideBar from '../../../components/common/sideBar/primarySideBar';
import AddButtonSection from '../../../components/common/AddButtonSection';
import InputIterator from '../../../components/common/inputIterator';
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { commonPostApi, commonPutApi, commonDeleteApi } from "../../../utils/apiService";
import { API_ENDPOINTS } from "../../../utils/apiEndpoints";
import { showToast } from '../../../components/common/toast';
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../../constants/toast";
import { SORT_ORDER } from "../../../constants/sortOrder";
import CommonModal from '../../../components/common/commonModel';
import './ManageNotification.scss';
import { convertToSearchField, DateandTimeConvert } from "../../../utils/utils";

const ManageNotification = () => {
  const navigate = useNavigate();
  const {
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    handleSubmit,
  } = useForm({});

  const [userData, setUserData] = useState([]);

  console.log("userData", userData);

  // Selection not needed for templates listing
  const [loading, setLoading] = useState(false);
  const [addTemplateVisible, setAddTemplateVisible] = useState(false);
  const [editTemplateVisible, setEditTemplateVisible] = useState(false);
  const [editTemplateDetails, setEditTemplateDetails] = useState({});
  const [loader, setLoader] = useState(false);
  const [visible, setVisible] = useState({ show: false });
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const userTimezoneOffset = new Date().getTimezoneOffset();
  const searchFeilds = ["template_name", "notification_title", "notification_description", "created_at"]
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 0,
    sortBy: 'created_at',
    sortDir: SORT_ORDER.DESC,
    timeCriteria: {
      timezone: userTimezone,
      timezoneOffset: userTimezoneOffset
    },
    searchCriteria: convertToSearchField(searchFeilds, "")
  });

  const userColumns = [
    { name: 'serial_number', label: 'S.No', sortable: true },
    { name: 'template_name', label: 'Template Name', sortable: true },
    { name: 'notification_title', label: 'Title', sortable: true },
    { name: 'notification_description', label: 'Description', sortable: true },
    { name: 'created_at', label: 'Date & Time', sortable: true },
  ];

  const templateFormRender = useMemo(() => ([
    {
      type: "text",
      label: "Template Name",
      name: "templateName",
      rules: {
        required: "Template name is required",
      },
      required: true,
    },
    {
      type: "text",
      label: "Title",
      name: "title",
      rules: {
        required: "Title is required",
      },
      required: true,
    },
    {
      type: "text",
      label: "Description",
      name: "description",
      rules: {
        required: "Description is required",
      },
      required: true,
    },
  ]), []);

  const button = [
    {
      type: "primarySuccessButton",
      label: "Add New Template",
      icon: "pi pi-plus",
      class: "mt-2 ml-2 w-full pr-3 md:pr-4",
      onClick: () => {
        navigate('/notifications/new-template');
      },
    },
  ];

  const AddTemplateButtons = [
    {
      type: "primarySuccessButton",
      label: "Submit",
      class: "col-4",
      disabled: Object.keys(dirtyFields).length === 0 || loader,
    },
    {
      type: "primaryCancelButton",
      label: "Cancel",
      class: "col-4",
      onClick: () => {
        setAddTemplateVisible(false);
        reset();
      },
    },
  ];

  const EditTemplateButtons = [
    {
      type: "primarySuccessButton",
      label: "Submit",
      class: "col-4",
      disabled: Object.keys(dirtyFields).length === 0 || loader,
      onClick: () => handleSubmit(onEditSubmit)(),
    },
    {
      type: "primaryCancelButton",
      label: "Cancel",
      class: "col-4",
      onClick: () => {
        setEditTemplateVisible(false);
        reset();
      },
    },
  ];

  const confirmDelete = (rowData) => {
    setTemplateToDelete(rowData);
    setVisible({ show: true });
  };

  const submitButtons = [
    {
      type: "primarySuccessButton",
      label: "Yes",
      class: "w-8rem pr-3",
      onClick: async () => {
        setVisible((prev) => ({
          ...prev,
          show: false,
        }));
        if (templateToDelete) {
          await handleDelete(templateToDelete);
        }
        setTemplateToDelete(null);
      },
    },
    {
      type: "secondaryButton",
      label: "No",
      class: "w-7rem",
      onClick: () => {
        setVisible((prev) => ({
          ...prev,
          show: false,
        }));
        setTemplateToDelete(null);
      },
    },
  ];

  useEffect(() => {
    getTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTemplates = async (navParams = navigator) => {
    try {
      setLoading(true);
      const queryData = {
        page: navParams.page,
        limit: navParams.rows,
        sortFields: [[navParams.sortBy, navParams.sortDir.toUpperCase()]],
        searchCriteria: navParams.searchCriteria,
        timeCriteria: {
          timezone: userTimezone,
          timezoneOffset: userTimezoneOffset
        }
      };
      const res = await commonPostApi(API_ENDPOINTS.GET_NOTIFICATION_TEMPLATES, { query: queryData });
      if (res?.data?.data) {
        const userdata = res.data.data.map(item => ({
          ...item,
          created_at: DateandTimeConvert(item.created_at),
          updated_at: DateandTimeConvert(item.updated_at),
        }));

        setUserData(userdata);

        setNavigator(prev => ({
          ...prev,
          totalRecords: res?.data?.total || 0,
          page: res?.data?.currentPage || prev.page,
        }));
      }
    } catch (err) {
      console.log('Get Templates Error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    const data = convertToSearchField(searchFeilds, value)
    const newNavigator = {
      ...navigator,
      page: 1,
      first: 0,
      searchCriteria: data,
    };
    setNavigator(newNavigator);
    getTemplates(newNavigator);
  };

  const onSortClick = (event) => {
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    const newNavigator = {
      ...navigator,
      sortBy: event?.sortField || 'created_at',
      sortDir: sortOrder,
    };
    setNavigator(newNavigator);
    getTemplates(newNavigator);
  };

  const onPageChange = (event) => {
    const newNavigator = {
      ...navigator,
      first: event?.first,
      page: event.page + 1,
    };
    setNavigator(newNavigator);
    getTemplates(newNavigator);
  };

  const onRowCountChange = (e) => {
    const newNavigator = {
      ...navigator,
      rows: e?.target?.value,
      page: 1,
      first: 0,
    };
    setNavigator(newNavigator);
    getTemplates(newNavigator);
  };

  const handleUse = (rowData) => {
    navigate('/notifications/send', {
      state: {
        title: rowData?.notification_title || '',
        description: rowData?.notification_description || ''
      }
    });
  };

  const handleEdit = (rowData) => {
    console.log("rowData", rowData);

    setEditTemplateDetails(rowData);
    setEditTemplateVisible(true);
  };

  const onEditSubmit = async (data) => {
    if (loader) return;
    const templateId = editTemplateDetails?.notification_template_id;
    if (!templateId) {
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.TEMPLATE_ID_NOT_FOUND);
      return;
    }

    if (!data?.templateName || !data?.title || !data?.description) {
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.REQUIRED_FIELDS);
      return;
    }

    const payload = {
      notification_title: data?.title,
      notification_description: data?.description,
      template_name: data?.templateName,
    };

    try {
      setLoader(true);
      const res = await commonPutApi(API_ENDPOINTS.EDIT_NOTIFICATION_TEMPLATE(templateId), payload);
      if (res?.status === 200 || res?.status === 201) {
        showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.NOTIFICATION.TEMPLATE_UPDATED);
        setEditTemplateVisible(false);
        reset();
        await getTemplates();
      } else {
        showToast(TOAST_TYPE.ERROR, res?.data?.message || COMMON_MSG.ERROR.GENERIC);
      }
    } catch (err) {
      console.error('Edit Template Error', err);
      showToast(TOAST_TYPE.ERROR, err?.response?.data?.message || COMMON_MSG.ERROR.GENERIC);
    } finally {
      setLoader(false);
    }
  };

  const handleDelete = async (rowData) => {
    const templateId = rowData?.notification_template_id;

    try {
      setLoading(true);
      const res = await commonDeleteApi(API_ENDPOINTS.DELETE_NOTIFICATION_TEMPLATE(templateId));
      if (res?.status === 200 || res?.status === 201) {
        showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.NOTIFICATION.TEMPLATE_DELETED);
        await getTemplates();
      } else {
        showToast(TOAST_TYPE.ERROR, res?.data?.message || COMMON_MSG.ERROR.GENERIC);
      }
    } catch (err) {
      console.error('Delete Template Error', err);
      showToast(TOAST_TYPE.ERROR, err?.response?.data?.message || COMMON_MSG.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  const itemRenderer = (item, prop) => (
    <div className="p-menuitem-content">
      {item.label === "Use" && (
        <Button
          className="card-button-assign-role"
          label="Use"
          onClick={() => {
            handleUse(prop?.props?.itemProp);
          }}
        />
      )}
      {item.label === "Edit" && (
        <Button
          className="card-button-edit-secound"
          label="Edit"
          onClick={() => {
            handleEdit(prop?.props?.itemProp);
          }}
        />
      )}
      {item.label === "Delete" && (
        <Button
          className="card-button-delete"
          label="Delete"
          onClick={() => {
            confirmDelete(prop?.props?.itemProp);
          }}
        />
      )}
    </div>
  );

  useEffect(() => {
    if (!editTemplateVisible) return;
    setValue('templateName', editTemplateDetails?.template_name ?? '');
    setValue('title', editTemplateDetails?.notification_title ?? '');
    setValue('description', editTemplateDetails?.notification_description ?? '');
  }, [editTemplateDetails, editTemplateVisible, setValue]);

  return <>
    <div className="container notification-manage-container">
      <PrimaryHeading className={"mt-5 mb-3"} />
      <div className="mt-3 md:container" style={{ color: "rgba(47, 43, 61, 0.9)" }}>
        <DataGrid
          header={
            <ButtonWithSearch
              buttons={button}
              searchHeaderLable={"Manage Template"}
              searchPlaceHolder={"Search Templates"}
              isSearchEnabled={true}
              isCalenderEnabled={false}
              isButtonEnabledLeft={false}
              isButtonEnabledRight={true}
              onChange={handleSearchChange}
            />
          }
          column={userColumns}
          value={userData}
          dataKey="id"
          sortable={true}
          // filterColumn={['sno', 'templateName', 'title', 'description', 'dateTime']}
          showDownloadButton={false}
          navigator={navigator}
          onSortClick={onSortClick}
          onPageChange={onPageChange}
          onRowCountChange={onRowCountChange}
          loading={loading}
          selectable={false}
          isPaginatorEnabled={true}
          customColumn={['serial_number']}
          columnCallBack={(rowData, column) => {
            if (column.name === 'serial_number') {
              const rowIndex = userData.findIndex(item => item === rowData);
              const currentPage = navigator.page;
              const rowsPerPage = navigator.rows;
              const serialNumber = (currentPage - 1) * rowsPerPage + rowIndex + 1;
              return serialNumber;
            }
            return rowData[column.name];
          }}
          optionsView={[
            {
              items: [
                { label: "Use", icon: "pi pi-send", template: itemRenderer },
                { label: "Edit", icon: "pi pi-pencil", template: itemRenderer },
                { label: "Delete", icon: "pi pi-trash", template: itemRenderer },
              ],
            },
          ]}
        />
      </div>
      {addTemplateVisible && (
        <PrimarySideBar
          visible={addTemplateVisible}
          onHide={() => setAddTemplateVisible(false)}
          header={<div className="sideBar-header">Add New Template</div>}
        >
          <InputIterator
            control={control}
            errors={errors}
            formRender={templateFormRender}
          />
          <div className="pt-3">
            <AddButtonSection buttons={AddTemplateButtons} />
          </div>
        </PrimarySideBar>
      )}
      {editTemplateVisible && (
        <PrimarySideBar
          visible={editTemplateVisible}
          onHide={() => setEditTemplateVisible(false)}
          header={<div className="sideBar-header">Edit Template</div>}
        >
          <InputIterator
            control={control}
            errors={errors}
            formRender={templateFormRender}
          />
          <div className="pt-3">
            <AddButtonSection buttons={EditTemplateButtons} />
          </div>
        </PrimarySideBar>
      )}
      <CommonModal
        visible={visible?.show}
        header={
          <div className="flex align-items-center gap-2">
            <span>Delete Notification</span>
          </div>
        }
        onHide={() => {
          setVisible((prev) => ({
            ...prev,
            show: false,
          }));
          setTemplateToDelete(null);
        }}
      >
        <div>
          <div className="flex justify-content-center pr-4">
            Would you like to delete this Notification?
          </div>

          <div className="mt-6 ml-2">
            <AddButtonSection buttons={submitButtons} />
          </div>
        </div>
      </CommonModal>
    </div>
  </>;
};

export default ManageNotification;